/**
 * Длинная сторона после сжатия в браузере. Сервер всё равно приведёт фото к
 * 1600 px; здесь важно пролезть через предел nginx (10 МБ на запрос) и не
 * гнать 20 МБ с фотосессии по медленной связи на сервер с одним ядром.
 */
export const SHRINK_MAX_DIMENSION = 2400;

/** Тяжелее этого пересохраняем, даже если по размеру в пикселях фото проходит. */
export const SHRINK_MAX_BYTES = 9 * 1024 * 1024;

const JPEG_QUALITY = 0.92;

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = (): void => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = (): void => {
      URL.revokeObjectURL(url);
      reject(new Error('Браузер не смог прочитать изображение'));
    };
    image.src = url;
  });

/**
 * Возвращает файл, готовый к отправке: тот же, если сжимать незачем, или
 * JPEG не больше 2400 px.
 *
 * Рисуем через <img>, а не createImageBitmap: при отрисовке картинки на
 * canvas браузеры применяют поворот из EXIF (image-orientation: from-image
 * по умолчанию), и naturalWidth/naturalHeight уже даны с учётом поворота.
 */
export async function shrinkImage(file: File): Promise<File> {
  const image = await loadImage(file);
  const longSide = Math.max(image.naturalWidth, image.naturalHeight);

  if (longSide <= SHRINK_MAX_DIMENSION && file.size <= SHRINK_MAX_BYTES) {
    return file;
  }

  const scale = Math.min(1, SHRINK_MAX_DIMENSION / longSide);
  const width = Math.round(image.naturalWidth * scale);
  const height = Math.round(image.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Браузер не дал нарисовать изображение');
  }
  // Белый фон: прозрачный PNG в JPEG иначе станет чёрным.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) {
    throw new Error('Не удалось сжать изображение');
  }

  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}
